import { clamp } from '../core/math';
import type { Rng } from '../core/rng';
import { danger } from './fire';
import type { FlowField } from './flowfield';
import { tryBoard } from './traffic';
import {
  CellState,
  GRID_H,
  GRID_W,
  House,
  PKind,
  PState,
  Person,
  RESOLVED,
  T,
  TERRAIN,
  idx,
  inBounds,
} from './types';
import type { World } from './world';

const BASE_SPEED = 0.77; // cells per second on flat ground
const UPHILL = 0.55;
const DOWNHILL = 1.2;
/** Seconds between a person reconsidering their escape plan. */
const THINK_EVERY = 3;

export function makePeople(houses: readonly House[], rng: Rng, stubbornFrac: number): Person[] {
  const people: Person[] = [];
  let group = 0;
  for (const h of houses) {
    for (let n = 0; n < h.residents; n++) {
      if (n % 4 === 0) group++;
      people.push({
        id: people.length,
        x: h.x + rng.range(0.4, h.w - 0.4),
        y: h.y + rng.range(0.4, h.h - 0.4),
        houseId: h.id,
        group,
        state: PState.Home,
        speed: BASE_SPEED * rng.range(0.78, 1.22),
        health: 1,
        stubborn: rng.chance(stubbornFrac),
        carId: -1,
        roadId: -1,
        think: rng.range(0, THINK_EVERY),
      });
    }
  }
  return people;
}

export function alertAll(w: World): void {
  let n = 0;
  for (const p of w.people) {
    if (p.state === PState.Home) {
      p.state = PState.ToRoad;
      p.think = THINK_EVERY; // decide immediately
      p.stubborn = false;
      n++;
    }
  }
  w.say(`General evacuation ordered. ${n} people are on the move.`, 'warn');
}

export function stepPeople(w: World, dt: number): void {
  const { grid, stats } = w;
  stats.home = 0;
  stats.fleeing = 0;
  stats.inCar = 0;
  stats.waiting = 0;
  stats.aboard = 0;
  stats.safe = 0;
  stats.rescued = 0;
  stats.lost = 0;
  stats.injured = 0;

  for (const p of w.people) {
    if (RESOLVED(p.state)) {
      if (p.state === PState.Safe) stats.safe++;
      else if (p.state === PState.Rescued) stats.rescued++;
      else stats.lost++;
      continue;
    }
    if (p.state === PState.Aboard) {
      stats.aboard++;
      p.x = w.heli.x / 4;
      p.y = w.heli.y / 4;
      continue;
    }

    // --- Heat ------------------------------------------------------------
    const cx = clamp(Math.floor(p.x), 0, GRID_W - 1);
    const cy = clamp(Math.floor(p.y), 0, GRID_H - 1);
    const here = idx(cx, cy);
    if (p.state !== PState.Waiting) {
      let hurt = 0;
      if (grid.state[here] === CellState.Burning) hurt += 0.55;
      else {
        let adjacent = 0;
        for (let dy = -1; dy <= 1; dy++)
          for (let dx = -1; dx <= 1; dx++) {
            if (!inBounds(cx + dx, cy + dy)) continue;
            if (grid.state[idx(cx + dx, cy + dy)] === CellState.Burning) adjacent++;
          }
        hurt += adjacent * 0.055;
        hurt += danger(grid, cx, cy) * 0.04;
      }
      if (hurt > 0) {
        p.health -= hurt * dt;
        if (p.health < 0.7 && w.rng.chance(dt * 2)) {
          w.spawnParticle(p.x * 4, p.y * 4, w.rng.range(-6, 6), -10, 0.7, PKind.Smoke);
        }
        if (p.health <= 0) {
          p.state = PState.Lost;
          stats.lost++;
          w.say('Casualty on the mountain.', 'bad');
          continue;
        }
      }
      if (p.health < 0.55) stats.injured++;
    }

    // --- Alerting --------------------------------------------------------
    if (p.state === PState.Home) {
      const fd = w.fireDistance(p.x, p.y);
      const radius = w.diff.alertRadius * (p.stubborn ? 0.35 : 1);
      const houseBurning = grid.state[here] === CellState.Burning;
      if (fd < radius || houseBurning || p.health < 0.99) {
        p.state = PState.ToRoad;
        p.think = THINK_EVERY;
      } else {
        stats.home++;
        continue;
      }
    }

    if (p.state === PState.InCar) {
      stats.inCar++;
      continue;
    }
    if (p.state === PState.Waiting) {
      stats.waiting++;
      continue;
    }
    stats.fleeing++;

    // --- Reconsider ------------------------------------------------------
    p.think += dt;
    if (p.think >= THINK_EVERY) {
      p.think = 0;
      decide(w, p, here);
    }

    // --- Move ------------------------------------------------------------
    const field = p.state === PState.ToRoad && p.roadId >= 0 ? w.fields.road[p.roadId] : w.fields.summit;
    move(w, p, field, dt);

    // --- Arrival ---------------------------------------------------------
    const nx = clamp(Math.floor(p.x), 0, GRID_W - 1);
    const ny = clamp(Math.floor(p.y), 0, GRID_H - 1);
    const ni = idx(nx, ny);

    if (p.state === PState.ToRoad && p.roadId >= 0 && grid.terrain[ni] === T.Road) {
      const road = w.roads[p.roadId];
      let bestS = -1;
      let bestD = Infinity;
      road.cells.forEach((c, s) => {
        const d = Math.hypot(c.x - p.x, c.y - p.y);
        if (d < bestD) {
          bestD = d;
          bestS = s;
        }
      });
      if (bestS >= 0 && bestD < 1.6 && !Number.isFinite(road.blocked[bestS])) {
        tryBoard(w, p, road, bestS);
      }
    } else if (p.state === PState.Walking && inAssembly(w, nx, ny)) {
      p.state = PState.Waiting;
      const spot = w.nextAssemblySpot();
      p.x = spot.x;
      p.y = spot.y;
    }
  }

  stats.total = w.people.length;
}

function inAssembly(w: World, x: number, y: number): boolean {
  const t = w.grid.terrain[idx(x, y)];
  if (t !== T.Helipad) return false;
  const pad = w.mtn.helipad;
  return x >= pad.x - 1 && x <= pad.x + pad.w && y >= pad.y - 1 && y <= pad.y + pad.h;
}

/**
 * Road or ridge? People would rather drive out, but they walk uphill instead if
 * the road is cut by fire, the queue has stopped moving, or there is no way
 * through to the road at all.
 */
function decide(w: World, p: Person, here: number): void {
  const summitCost = w.fields.summit.cost[here];
  const walkSeconds = Number.isFinite(summitCost) ? summitCost / Math.max(0.15, p.speed) : Infinity;

  let bestRoad = -1;
  let bestRoadSeconds = Infinity;
  for (const road of w.roads) {
    const field = w.fields.road[road.id];
    const cost = field.cost[here];
    if (!Number.isFinite(cost)) continue;
    // Walk to the road, then drive out, plus the time lost in the queue.
    const toRoad = cost / Math.max(0.15, p.speed);
    const driveCells = road.cells.length * 0.45;
    const drive = driveCells / 3.0 + road.jam * 55 + w.carsOnRoad(road.id) * 1.5;
    const seconds = (toRoad + drive) * 0.8; // people expect the road to be quicker than it is
    if (seconds < bestRoadSeconds) {
      bestRoadSeconds = seconds;
      bestRoad = road.id;
    }
  }

  if (bestRoad >= 0 && bestRoadSeconds < walkSeconds) {
    if (p.state !== PState.ToRoad || p.roadId !== bestRoad) {
      p.state = PState.ToRoad;
      p.roadId = bestRoad;
    }
  } else if (p.state !== PState.Walking) {
    p.state = PState.Walking;
    p.roadId = -1;
  }
}

function move(w: World, p: Person, field: FlowField, dt: number): void {
  const cx = clamp(Math.floor(p.x), 0, GRID_W - 1);
  const cy = clamp(Math.floor(p.y), 0, GRID_H - 1);
  const here = idx(cx, cy);
  const d = field.dir[here];

  let dx: number;
  let dy: number;
  if (d >= 0) {
    const nb = NB[d];
    dx = nb.dx;
    dy = nb.dy;
  } else {
    // No route out: head for higher ground, which may not help.
    dx = 0;
    dy = -1;
  }

  // Never knowingly step into flame.
  if (isFlame(w, cx + dx, cy + dy)) {
    const alts: [number, number][] = [
      [dx === 0 ? 1 : 0, dy === 0 ? 1 : dy],
      [dx === 0 ? -1 : 0, dy === 0 ? -1 : dy],
      [dx, 0],
      [0, dy],
    ];
    let found = false;
    for (const [ax, ay] of alts) {
      if ((ax !== 0 || ay !== 0) && !isFlame(w, cx + ax, cy + ay) && passable(w, cx + ax, cy + ay)) {
        dx = ax;
        dy = ay;
        found = true;
        break;
      }
    }
    if (!found) return; // trapped, nowhere safe to step
  }
  if (!passable(w, cx + dx, cy + dy)) return;

  const slope = dy < 0 ? UPHILL : dy > 0 ? DOWNHILL : 1;
  const terrain = TERRAIN[w.grid.terrain[idx(clamp(cx + dx, 0, GRID_W - 1), clamp(cy + dy, 0, GRID_H - 1))]];
  const injured = p.health < 0.55 ? 0.6 : 1;
  const v = p.speed * slope * injured / Math.max(0.5, terrain.walk);

  // Aim at the centre of the next cell so the walker looks like it is following the track.
  const tx = cx + dx + 0.5;
  const ty = cy + dy + 0.5;
  const len = Math.hypot(tx - p.x, ty - p.y) || 1;
  p.x = clamp(p.x + ((tx - p.x) / len) * v * dt, 0.5, GRID_W - 0.5);
  p.y = clamp(p.y + ((ty - p.y) / len) * v * dt, 0.5, GRID_H - 0.5);
}

const isFlame = (w: World, x: number, y: number): boolean =>
  inBounds(x, y) && w.grid.state[idx(x, y)] === CellState.Burning;

const passable = (w: World, x: number, y: number): boolean =>
  inBounds(x, y) && Number.isFinite(TERRAIN[w.grid.terrain[idx(x, y)]].walk) && y < GRID_H;

// Local copy of the neighbour table to keep this module free of render imports.
const NB = [
  { dx: -1, dy: -1 },
  { dx: 0, dy: -1 },
  { dx: 1, dy: -1 },
  { dx: -1, dy: 0 },
  { dx: 1, dy: 0 },
  { dx: -1, dy: 1 },
  { dx: 0, dy: 1 },
  { dx: 1, dy: 1 },
] as const;
